import {
  Content,
  Item,
  ItemIndicator,
  Portal,
  RadioGroup,
  RadioItem,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { Slottable } from '@radix-ui/react-slot';
import clsx from 'clsx';
import {
  cloneElement,
  ComponentPropsWithRef,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { CheckIcon } from '../icons/react/check';
import { Spinner } from '../spinner/spinner';
import { space } from '../tokens';
import {
  criticalDropdownItem,
  dropdownMenuContent,
  dropdownMenuItem,
  dropdownMenuItemIndicator,
  dropdownMenuSeparator,
  dropdownMenuItemRightIcon,
} from './dropdown-menu.css';

export const DropdownMenuRoot = Root;

export const DropdownMenuTrigger = function DropdownMenuTrigger({
  ref,
  ...props
}: ComponentPropsWithRef<typeof Trigger>) {
  return <Trigger asChild ref={ref} {...props} />;
};

export const DropdownMenuContent = function DropdownMenuContent({
  ref,
  className,
  ...rest
}: ComponentPropsWithRef<typeof Content>) {
  return (
    <Portal>
      <Content
        ref={ref}
        className={clsx(dropdownMenuContent, className)}
        side="bottom"
        align="start"
        collisionPadding={space.s12}
        sideOffset={space.s4}
        {...rest}
      />
    </Portal>
  );
};

export const DropdownMenuItem = function DropdownMenuItem({
  ref,
  className,
  icon,
  iconPosition = 'left',
  variant = 'default',
  loading = false,
  disabled,
  asChild = false,
  children,
  ...rest
}: ComponentPropsWithRef<typeof Item> & {
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'critical';
  loading?: boolean;
}) {
  const renderIcon = loading ? <Spinner size={16} /> : icon;
  const showIcon = loading || icon;

  const childrenContent =
    asChild && isValidElement<PropsWithChildren>(children)
      ? children.props.children
      : children;

  const content = (
    <>
      {iconPosition === 'left' && showIcon && renderIcon}
      <Slottable>{childrenContent}</Slottable>
      {iconPosition === 'right' && showIcon && (
        <span className={dropdownMenuItemRightIcon}>{renderIcon}</span>
      )}
    </>
  );

  return (
    <Item
      ref={ref}
      className={clsx(
        dropdownMenuItem,
        variant === 'critical' && criticalDropdownItem,
        className,
      )}
      disabled={disabled || loading}
      asChild={asChild}
      {...rest}
    >
      {asChild && isValidElement(children)
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cloneElement(children, children.props as any, content)
        : content}
    </Item>
  );
};

export const DropdownMenuSeparator = function DropdownMenuSeparator({
  ref,
  className,
  ...props
}: ComponentPropsWithRef<typeof Separator>) {
  return (
    <Separator
      ref={ref}
      className={clsx(dropdownMenuSeparator, className)}
      {...props}
    />
  );
};

export const DropdownMenuSub = function DropdownMenuSub({
  ...props
}: ComponentPropsWithRef<typeof Sub>) {
  return <Sub {...props} />;
};

export const DropdownMenuSubTrigger = function DropdownMenuSubTrigger({
  ...props
}: ComponentPropsWithRef<typeof SubTrigger>) {
  return <SubTrigger {...props} />;
};

export const DropdownMenuSubContent = function DropdownMenuSubContent({
  ref,
  className,
  ...rest
}: ComponentPropsWithRef<typeof SubContent>) {
  return (
    <Portal>
      <SubContent
        ref={ref}
        className={clsx(dropdownMenuContent, className)}
        collisionPadding={space.s12}
        sideOffset={space.s4}
        {...rest}
      />
    </Portal>
  );
};

export const DropdownMenuRadioGroup = RadioGroup;

export const DropdownMenuRadioItem = function DropdownMenuRadioItem({
  ref,
  className,
  icon,
  children,
  ...rest
}: ComponentPropsWithRef<typeof RadioItem> & {
  icon?: ReactNode;
}) {
  return (
    <RadioItem
      ref={ref}
      className={clsx(dropdownMenuItem, className)}
      {...rest}
    >
      {icon}
      {children}
      <DropdownMenuItemIndicator>
        <CheckIcon />
      </DropdownMenuItemIndicator>
    </RadioItem>
  );
};

export const DropdownMenuItemIndicator = function DropdownMenuItemIndicator({
  ref,
  className,
  ...rest
}: ComponentPropsWithRef<typeof ItemIndicator>) {
  return (
    <ItemIndicator
      ref={ref}
      className={clsx(dropdownMenuItemIndicator, className)}
      {...rest}
    />
  );
};
